// Estado local do canvas do Team Map (flowchart estilo n8n).
// Signals locais ao módulo — o canvas é singleton na rota. Reset no mount via initFlow().

import { signal } from '@preact/signals';
import type { FlowNodeModel } from './layout';
import { buildGraph, type FlowGraph } from './layout';
import type { TeamSummary, AgentSummary } from '../../types/agno-api';

// Override de posições por drag. Quando vazio para um id, usa a posição inicial do layout.
export const positions = signal<Map<string, { x: number; y: number }>>(new Map());

export const pan = signal<{ x: number; y: number }>({ x: 0, y: 0 });
export const zoom = signal<number>(1); // clamp [0.4, 2.0]
// Default expandido: tools visíveis ao abrir o Team Map (cada tool vira nó na
// coluna 3, ligada ao seu agente). O toggle na Toolbar continua recolhendo.
export const toolsCollapsed = signal<boolean>(false);
export const draggingId = signal<string | null>(null);

// Grafo corrente (nós + edges) — recomputado em troca de team/toolsCollapsed.
export const graph = signal<FlowGraph>({ nodes: [], edges: [] });

export const ZOOM_MIN = 0.4;
export const ZOOM_MAX = 2.0;

export function clampZoom(z: number): number {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
}

// Único ponto de conversão tela→mundo. Usado por drag-de-nó e zoom-to-pointer.
export function screenToWorld(
  clientX: number,
  clientY: number,
  rect: DOMRect,
): { x: number; y: number } {
  return {
    x: (clientX - rect.left - pan.value.x) / zoom.value,
    y: (clientY - rect.top - pan.value.y) / zoom.value,
  };
}

// Posição efetiva de um nó: override em `positions` ou a posição inicial do layout.
export function getPos(node: FlowNodeModel): { x: number; y: number } {
  return positions.value.get(node.id) ?? { x: node.x, y: node.y };
}

// (Re)constrói o grafo e zera o estado de viewport. Chamado no mount e em troca de team.
export function initFlow(team: TeamSummary, members: AgentSummary[]): void {
  graph.value = buildGraph(team, members, toolsCollapsed.value);
  positions.value = new Map();
  pan.value = { x: 0, y: 0 };
  zoom.value = 1;
  draggingId.value = null;
}

// Recomputa só o layout (posições iniciais) mantendo o viewport. Usado no toggle de tools.
export function relayout(team: TeamSummary, members: AgentSummary[]): void {
  graph.value = buildGraph(team, members, toolsCollapsed.value);
  positions.value = new Map();
}
